/**
 * 🌪️ useCreaturePersonality Hook
 * React integration for the creature personality system.
 * 
 * Created by Tom & Azura - January 14, 2026
 * ❤️⚡️❤️
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { CreaturePersonality } from '../CreaturePersonality';
import type { 
  Temperament, 
  PersonalityState, 
  UseCreaturePersonalityReturn,
  StimulusType,
  Mood,
} from '../types';

export function useCreaturePersonality(
  temperament: Temperament,
  autoStart: boolean = true
): UseCreaturePersonalityReturn {
  
  // Create initial state
  const createInitialState = (): PersonalityState => ({
    mood: temperament.initialMood,
    previousMood: null,
    energy: temperament.energy.initial,
    corner: 'center',
    moodStartTime: Date.now(),
    moodTickCount: 0,
    expression: {
      rotation: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 150,
      glowIntensity: 0,
      glowColor: 'transparent',
    },
    bulge: { x: 50, y: 50, scale: 0, opacity: 0 },
    isRunning: false,
  });

  const [state, setState] = useState<PersonalityState>(createInitialState);
  const engineRef = useRef<CreaturePersonality | null>(null);

  // Initialize engine
  useEffect(() => {
    const engine = new CreaturePersonality(temperament, (newState) => {
      setState({ ...newState });
    });
    
    engineRef.current = engine;

    if (autoStart) {
      engine.start();
    }

    return () => {
      engine.stop();
    };
  }, [temperament, autoStart]);

  // Trigger stimulus
  const triggerStimulus = useCallback((
    type: StimulusType,
    intensity: number = 1,
    data?: unknown
  ) => {
    engineRef.current?.triggerStimulus(type, intensity, data);
  }, []);

  // Force mood
  const forceMood = useCallback((mood: Mood, duration?: number) => {
    engineRef.current?.forceMood(mood, duration);
  }, []);

  // Start
  const start = useCallback(() => {
    engineRef.current?.start();
  }, []);

  // Stop
  const stop = useCallback(() => {
    engineRef.current?.stop();
  }, []);

  // Reset
  const reset = useCallback(() => {
    engineRef.current?.reset();
  }, []);

  // Get indicators
  const indicators = engineRef.current?.getMoodIndicators() || {
    emoji: '❓',
    text: '???',
    label: '',
  };

  return {
    state,
    mood: state.mood,
    energy: state.energy,
    expression: state.expression,
    bulge: state.bulge,
    indicators,
    triggerStimulus,
    forceMood,
    start,
    stop,
    reset,
  };
}
